import { Component, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, GridApi } from 'ag-grid-community';
import { ProduitService } from '../../services/produit.service';
import { MiseadispoService } from '../../services/miseadispo.service';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { ConfirmationService } from '../../services/confirmation.service';
import { MagasinSimulationService } from '../../services/magasin-simulation.service';
import { Produit } from '../../models/produit.model';
import { User } from '../../models/user.model';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-produits',
  standalone: true,
  imports: [AgGridAngular, FormsModule, DatePipe],
  templateUrl: './produits.component.html',
  styleUrl: './produits.component.scss'
})
export class ProduitsComponent implements OnInit {
  produits: Produit[] = [];
  isLoading = true;
  searchText = '';
  private gridApi!: GridApi;
  selectedProducts: Produit[] = [];
  currentDate = new Date();
  showQuantityDialog = false;
  productQuantities: Map<number, number> = new Map();
  magasinsUsers: User[] = [];
  selectedMagasinId: number | null = null;
  isSuperAdmin = false;

  columnDefs: ColDef[] = [
    {
      headerName: 'Titre',
      field: 'titre',
      flex: 1,
      minWidth: 200,
      cellStyle: { fontWeight: '600' }
    },
    {
      headerName: 'Titre commercial',
      field: 'titre_commercial',
      flex: 1,
      minWidth: 200,
      valueFormatter: (params) => params.value || '-'
    },
    {
      headerName: 'PLU',
      field: 'plu',
      width: 150,
      cellRenderer: (params: any) => {
        return `<span style="background: #edf2f7; color: #4a5568; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600;">${params.value}</span>`;
      }
    },
    {
      headerName: 'Famille',
      field: 'familleId',
      width: 120,
      valueFormatter: (params) => `Famille ${params.value}`
    },
    {
      headerName: 'Statut',
      field: 'is_activated',
      width: 120,
      cellRenderer: (params: any) => {
        const isActive = params.value;
        const bgColor = isActive ? '#c6f6d5' : '#fed7d7';
        const textColor = isActive ? '#2f855a' : '#c53030';
        const label = isActive ? 'Actif' : 'Inactif';
        return `<span style="background: ${bgColor}; color: ${textColor}; padding: 6px 12px; border-radius: 12px; font-size: 12px; font-weight: 600;">${label}</span>`;
      }
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };

  rowSelection = {
    mode: 'multiRow' as const,
    checkboxes: true,
    headerCheckbox: true
  };
  paginationPageSize = 20;
  paginationPageSizeSelector = [10, 20, 50, 100];
  theme: 'legacy' = 'legacy';

  magasinSimulation = inject(MagasinSimulationService);

  constructor(
    private produitService: ProduitService,
    private miseadispoService: MiseadispoService,
    private authService: AuthService,
    private userService: UserService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadProduits();
    this.checkUserRole();
    this.loadMagasinsUsers();
  }

  checkUserRole(): void {
    const currentUser = this.authService.currentUser();
    this.isSuperAdmin = currentUser?.profils?.some(p => p.code === 'super_admin') || false;
  }

  getMagasinId(user: User | null): number | null {
    if (!user) return null;

    let appData = user.app_data;

    // Parser si c'est une string
    if (typeof appData === 'string') {
      try {
        appData = JSON.parse(appData);
      } catch (e) {
        return null;
      }
    }

    return (appData as any)?.labase?.magasinId || null;
  }

  loadMagasinsUsers(): void {
    this.userService.getAllUsers().subscribe({
      next: (users) => {
        // Filtrer les utilisateurs avec le profil "magasin" ET un magasinId valide
        this.magasinsUsers = users
          .filter(user => user.profils?.some(p => p.code === 'magasin'))
          .filter(user => this.getMagasinId(user) !== null);
      },
      error: (error) => {
        console.error('Erreur lors du chargement des utilisateurs magasin:', error);
      }
    });
  }

  loadProduits(): void {
    this.produitService.getAllProduits().subscribe({
      next: (produits) => {
        this.produits = produits;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur chargement produits:', error);
        this.isLoading = false;
      }
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  onSearch(): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', this.searchText);
    }
  }

  onSelectionChanged(event: any): void {
    this.selectedProducts = event.api.getSelectedRows();
  }

  async mettreADisposition(): Promise<void> {
    if (this.selectedProducts.length === 0) {
      return;
    }

    const currentUser = this.authService.currentUser();

    // Si super_admin, aucune vérification de magasinId nécessaire
    if (!this.isSuperAdmin && !this.getMagasinId(currentUser)) {
      await this.confirmationService.confirm({
        title: 'Erreur',
        message: 'Aucun magasin associé à votre compte',
        confirmText: 'OK',
        cancelText: ''
      });
      return;
    }

    // Initialiser les quantités à 1 pour chaque produit
    this.productQuantities.clear();
    this.selectedProducts.forEach(produit => {
      const plu = typeof produit.plu === 'string' ? parseInt(produit.plu, 10) : produit.plu;
      this.productQuantities.set(plu, 1);
    });

    // Pour super_admin en simulation, pré-sélectionner le magasin simulé
    if (this.isSuperAdmin) {
      this.selectedMagasinId = this.magasinSimulation.simulatedMagasinId();
    }

    // Afficher le dialog
    this.showQuantityDialog = true;
  }

  onMagasinChange(value: string): void {
    this.selectedMagasinId = value ? parseInt(value, 10) : null;
  }

  getPluAsNumber(plu: number | string): number {
    return typeof plu === 'string' ? parseInt(plu, 10) : plu;
  }

  updateQuantity(plu: number, value: string): void {
    const quantite = parseInt(value, 10);
    if (!isNaN(quantite) && quantite > 0) {
      this.productQuantities.set(plu, quantite);
    }
  }

  getProductDisplayName(produit: Produit): string {
    if (produit.titre_commercial &&
        produit.titre_commercial.trim() !== '' &&
        produit.titre_commercial.toLowerCase() !== 'nf') {
      return produit.titre_commercial;
    }
    return produit.titre || '';
  }

  cancelDialog(): void {
    this.showQuantityDialog = false;
    this.productQuantities.clear();
    this.selectedMagasinId = null;
  }

  async confirmMiseADisposition(): Promise<void> {
    const currentUser = this.authService.currentUser();

    // Déterminer le magasinId à utiliser
    let magasinId: number | null;

    if (this.isSuperAdmin) {
      // Super admin doit sélectionner un magasin
      if (!this.selectedMagasinId) {
        await this.confirmationService.confirm({
          title: 'Magasin requis',
          message: 'Veuillez sélectionner un magasin',
          confirmText: 'OK',
          cancelText: ''
        });
        return;
      }
      magasinId = this.selectedMagasinId;
    } else {
      // Utilisateur magasin utilise son magasinId dans app_data
      magasinId = this.getMagasinId(currentUser);
      if (!magasinId) {
        return;
      }
    }

    const products: Array<{plu: number, quantite: number}> = [];

    // Construire le tableau de produits avec les quantités
    this.selectedProducts.forEach(produit => {
      const plu = typeof produit.plu === 'string' ? parseInt(produit.plu, 10) : produit.plu;
      const quantite = this.productQuantities.get(plu) || 1;
      products.push({ plu, quantite });
    });

    // Appel API
    this.miseadispoService.createFromArticles(products, magasinId).subscribe({
      next: async () => {
        await this.confirmationService.confirm({
          title: 'Succès',
          message: `${products.length} produit(s) mis à disposition avec succès!`,
          confirmText: 'OK',
          cancelText: ''
        });
        this.showQuantityDialog = false;
        this.productQuantities.clear();
        this.selectedMagasinId = null;
        // Vider la sélection
        if (this.gridApi) {
          this.gridApi.deselectAll();
        }
        this.selectedProducts = [];
      },
      error: async (error) => {
        console.error('Erreur lors de la mise à disposition:', error);
        await this.confirmationService.confirm({
          title: 'Erreur',
          message: 'Erreur lors de la mise à disposition des produits',
          confirmText: 'OK',
          cancelText: ''
        });
      }
    });
  }

  formatDate(date: string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  printSelection(): void {
    let dataToExport: Produit[] = [];

    if (this.selectedProducts.length > 0) {
      // Si sélection, exporter la sélection
      dataToExport = this.selectedProducts;
    } else {
      // Récupérer toutes les lignes filtrées
      const allFilteredRows: Produit[] = [];
      this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
        if (node.data) {
          allFilteredRows.push(node.data);
        }
      });

      // Appliquer la pagination pour n'exporter que la page courante
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const pageSize = this.gridApi.paginationGetPageSize();
      const startRow = currentPage * pageSize;
      const endRow = startRow + pageSize;

      dataToExport = allFilteredRows.slice(startRow, endRow);
    }

    if (dataToExport.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    // Créer le PDF
    const doc = new jsPDF();

    // Titre
    doc.setFontSize(18);
    doc.setTextColor(26, 32, 44);
    doc.text('Liste des produits', 14, 20);

    // Sous-titre
    doc.setFontSize(11);
    doc.setTextColor(74, 85, 104);
    const subtitle = this.selectedProducts.length > 0
      ? `${this.selectedProducts.length} produit(s) sélectionné(s)`
      : `${dataToExport.length} produit(s) affiché(s)`;
    doc.text(subtitle, 14, 28);

    // Date
    doc.setFontSize(9);
    doc.setTextColor(113, 128, 150);
    doc.text(`Imprimé le ${this.formatDate(this.currentDate.toISOString())}`, 14, 35);

    // Préparer les données du tableau
    const tableData = dataToExport.map(produit => [
      this.getProductDisplayName(produit),
      produit.plu?.toString() || '-',
      `Famille ${produit.familleId || '-'}`,
      produit.is_activated ? 'Actif' : 'Inactif'
    ]);

    // Générer le tableau
    autoTable(doc, {
      startY: 40,
      head: [['Titre', 'PLU', 'Famille', 'Statut']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [247, 250, 252],
        textColor: [26, 32, 44],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [226, 232, 240]
      },
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.5
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      }
    });

    // Sauvegarder le PDF
    const filename = `produits_${new Date().getTime()}.pdf`;
    doc.save(filename);
  }
}
